/**
 * Push-to-talk voice broadcast — real-time team radio, one code path everywhere.
 *
 * Codec: libopus via WebAssembly (see ./opus.ts) — the same plain-WASM codec on
 * Android WebView, iOS WKWebView and desktop browsers. VOIP application + **DTX**
 * at ~9 kbps narrowband: clear, walkie-talkie-grade speech where silences between
 * words cost ≈0 bytes (DTX frames aren't transmitted at all).
 *
 * Capture and playback use only universally-supported Web Audio primitives:
 *   • TalkSender   — getUserMedia → AudioWorklet (ScriptProcessor fallback) →
 *     resample to 48 kHz → Int16 frames (60 ms) → opus encode → stream packets
 *     (+ µs timestamps). Auto-stops after 20 s, or 1.5 s of silence once it has
 *     heard the speaker. Plays `talk_record` on start, `talk_end` on stop.
 *   • TalkReceiver — opus-decode each packet and schedule the PCM on the Web
 *     Audio master bus at its timestamp, behind a 250 ms jitter buffer (so DTX
 *     silence gaps reproduce faithfully). Plays `talk_begin` on open, `talk_end`
 *     once the buffer has drained.
 *
 * Packets are independently decodable raw Opus frames and cross the wire as raw
 * `Uint8Array` (Lowlander/DataPack carries binary natively — no base64 bloat).
 */
import { audioContext, masterOut } from './context';
import { sfx } from './sfx';
import {
  createVoiceEncoder,
  createVoiceDecoder,
  preloadOpus,
  type OpusEncoder,
  type OpusDecoder,
} from './opus';

export { preloadOpus };

// ── Tuning ──────────────────────────────────────────────────────────────────
const ENC_RATE = 48_000; // Opus-native capture/encode rate
const FRAME_SAMPLES = (ENC_RATE * 60) / 1000; // 60 ms frames = 2880 samples
const BITRATE_BPS = 9_000; // narrowband VOIP; DTX drops silence to ≈0
const DTX_MAX_BYTES = 2; // opus returns ≤2-byte "don't transmit" frames under DTX
const MAX_TALK_MS = 20_000;
const SILENCE_HANGOVER_MS = 1_500;
const SILENCE_RMS = 0.015;
const PLAYBACK_BUFFER_MS = 250; // receiver jitter buffer
const RECEIVER_STALL_MS = 4_000;
const RECEIVER_MAX_MS = 30_000;
const WORKLET_CHUNK = 2_048; // samples the worklet batches before posting

/** The server side of a broadcast, injected by the store (RPC-backed). */
export interface TalkTransport {
  /** Charge + validate; resolves true iff the broadcast may proceed. */
  start(): Promise<boolean>;
  /** Relay one Opus packet (with its µs timestamp) to teammates. */
  data(bytes: Uint8Array, tsUs: number): void;
  /** Signal end of transmission. */
  end(): void;
}

// ── Capture worklet (inlined; loaded as a Blob so it bundles with no extra file) ─
const WORKLET_SRC = `
class TalkCaptureProcessor extends AudioWorkletProcessor {
  constructor() { super(); this._buf = new Float32Array(${WORKLET_CHUNK}); this._n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let i = 0; i < ch.length; i++) {
        this._buf[this._n++] = ch[i];
        if (this._n === this._buf.length) {
          const out = this._buf.slice(0);
          this.port.postMessage(out, [out.buffer]);
          this._n = 0;
        }
      }
    }
    return true;
  }
}
registerProcessor('talk-capture', TalkCaptureProcessor);
`;
let workletUrl: string | undefined;
function getWorkletUrl(): string {
  if (!workletUrl) {
    workletUrl = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }));
  }
  return workletUrl;
}

/** Streaming linear resampler (only used when the device can't capture at 48 kHz). */
class Resampler {
  private readonly step: number;
  private t = 0;
  private last = 0;
  constructor(inRate: number, outRate: number) {
    this.step = inRate / outRate;
  }
  process(inp: Float32Array): Float32Array {
    if (this.step === 1) return inp;
    const out: number[] = [];
    let t = this.t;
    const at = (i: number) => (i < 0 ? this.last : inp[i]);
    while (Math.floor(t) + 1 < inp.length) {
      const i = Math.floor(t);
      const frac = t - i;
      out.push(at(i) + (at(i + 1) - at(i)) * frac);
      t += this.step;
    }
    this.t = t - inp.length;
    this.last = inp[inp.length - 1];
    return Float32Array.from(out);
  }
}

// ── Sender ───────────────────────────────────────────────────────────────────
export class TalkSender {
  private active = false;
  private transport?: TalkTransport;
  private stream?: MediaStream;
  private capCtx?: AudioContext;
  private source?: MediaStreamAudioSourceNode;
  private workletNode?: AudioWorkletNode;
  private scriptNode?: ScriptProcessorNode;
  private resampler: Resampler | null = null;
  private encoder?: OpusEncoder;
  private readonly acc = new Float32Array(FRAME_SAMPLES);
  private accLen = 0;
  private readonly pcm = new Int16Array(FRAME_SAMPLES);
  private sentSamples = 0;
  private watchdog?: ReturnType<typeof setInterval>;
  private startMs = 0;
  private lastLoudMs = 0;
  private heard = false;

  /** Fired true the instant capture begins, false when it stops/fails. The store
   *  uses this to pause/resume speech recognition (mic contention + so it doesn't
   *  hear the talker's own voice). */
  onActiveChange?: (active: boolean) => void;

  isActive(): boolean {
    return this.active;
  }

  /** Begin a push-to-talk broadcast. Re-entry while active is ignored. */
  async begin(transport: TalkTransport): Promise<void> {
    if (this.active) return;
    this.active = true;
    this.transport = transport;
    this.heard = false;
    this.accLen = 0;
    this.sentSamples = 0;
    this.onActiveChange?.(true);
    void sfx.playOneShot('talk_record', 0);

    if (!navigator.mediaDevices?.getUserMedia || typeof AudioContext === 'undefined') {
      console.warn('[talk] audio capture unsupported here');
      this.fail();
      return;
    }

    const startP = transport.start();
    const micP = navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    let ok = false;
    let stream: MediaStream | undefined;
    try {
      ok = await startP;
    } catch {
      ok = false;
    }
    try {
      stream = await micP;
    } catch {
      stream = undefined;
    }

    if (!this.active) {
      stream?.getTracks().forEach((t) => t.stop());
      if (ok) transport.end();
      return;
    }
    if (!ok) {
      stream?.getTracks().forEach((t) => t.stop());
      this.fail();
      return;
    }
    if (!stream) {
      transport.end();
      this.fail();
      return;
    }
    this.stream = stream;

    try {
      this.encoder = await createVoiceEncoder(ENC_RATE, BITRATE_BPS);
    } catch (e) {
      console.warn('[talk] opus encoder init failed', e);
      transport.end();
      this.fail();
      return;
    }
    if (!this.active) {
      transport.end();
      this.teardown();
      return;
    }

    try {
      await this.startCapture(stream);
    } catch (e) {
      console.warn('[talk] capture start failed', e);
      transport.end();
      this.fail();
      return;
    }

    this.startMs = performance.now();
    this.lastLoudMs = this.startMs;
    this.watchdog = setInterval(() => this.tick(), 100);
  }

  /** External cancel (e.g. the game ended under us). */
  cancel(): void {
    if (this.active) this.stop();
  }

  // ── internals ──────────────────────────────────────────────────────────────
  private async startCapture(stream: MediaStream): Promise<void> {
    let ctx: AudioContext;
    try {
      ctx = new AudioContext({ sampleRate: ENC_RATE });
    } catch {
      ctx = new AudioContext();
    }
    this.capCtx = ctx;
    await ctx.resume().catch(() => {});
    this.resampler = ctx.sampleRate === ENC_RATE ? null : new Resampler(ctx.sampleRate, ENC_RATE);
    this.source = ctx.createMediaStreamSource(stream);

    if (ctx.audioWorklet) {
      try {
        await ctx.audioWorklet.addModule(getWorkletUrl());
        const node = new AudioWorkletNode(ctx, 'talk-capture');
        node.port.onmessage = (e: MessageEvent) => this.onPcmChunk(e.data as Float32Array);
        this.source.connect(node);
        node.connect(ctx.destination); // pull the node; it emits no audio
        this.workletNode = node;
        return;
      } catch (e) {
        console.warn('[talk] AudioWorklet unavailable, using ScriptProcessor', e);
      }
    }
    // Fallback for engines without AudioWorklet.
    const sp = ctx.createScriptProcessor(WORKLET_CHUNK, 1, 1);
    sp.onaudioprocess = (e: AudioProcessingEvent) =>
      this.onPcmChunk(e.inputBuffer.getChannelData(0).slice(0));
    this.source.connect(sp);
    sp.connect(ctx.destination); // ScriptProcessor needs a sink to be pulled
    this.scriptNode = sp;
  }

  private onPcmChunk(chunk: Float32Array): void {
    if (!this.active || !this.encoder) return;
    const r = this.resampler ? this.resampler.process(chunk) : chunk;
    let off = 0;
    while (off < r.length) {
      const take = Math.min(FRAME_SAMPLES - this.accLen, r.length - off);
      this.acc.set(r.subarray(off, off + take), this.accLen);
      this.accLen += take;
      off += take;
      if (this.accLen === FRAME_SAMPLES) {
        this.encodeFrame();
        this.accLen = 0;
      }
    }
  }

  private encodeFrame(): void {
    const frame = this.acc;
    let sum = 0;
    for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
    if (Math.sqrt(sum / frame.length) >= SILENCE_RMS) {
      this.heard = true;
      this.lastLoudMs = performance.now();
    }
    for (let i = 0; i < frame.length; i++) {
      let s = frame[i];
      s = s < -1 ? -1 : s > 1 ? 1 : s;
      this.pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    let packet: Uint8Array;
    try {
      packet = this.encoder!.encode(this.pcm);
    } catch {
      this.sentSamples += FRAME_SAMPLES;
      return;
    }
    const tsUs = Math.round((this.sentSamples / ENC_RATE) * 1e6);
    this.sentSamples += FRAME_SAMPLES;
    // DTX: libopus returns a ≤2-byte frame when it decides silence needn't be
    // sent — skip it; the receiver reproduces the gap from timestamps.
    if (packet.length > DTX_MAX_BYTES) this.transport?.data(packet, tsUs);
  }

  private tick(): void {
    const now = performance.now();
    const elapsed = now - this.startMs;
    const silentLongEnough = this.heard && now - this.lastLoudMs >= SILENCE_HANGOVER_MS;
    if (elapsed >= MAX_TALK_MS || silentLongEnough) this.stop();
  }

  private stop(): void {
    if (!this.active) return;
    this.active = false;
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = undefined;
    void sfx.playOneShot('talk_end', 0);
    this.onActiveChange?.(false);
    this.transport?.end();
    this.teardown();
  }

  private fail(): void {
    this.active = false;
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = undefined;
    void sfx.playOneShot('beep_cancel', 0);
    this.onActiveChange?.(false);
    this.teardown();
  }

  private teardown(): void {
    try {
      this.workletNode?.disconnect();
      this.workletNode?.port.close();
    } catch {
      /* noop */
    }
    try {
      if (this.scriptNode) this.scriptNode.onaudioprocess = null;
      this.scriptNode?.disconnect();
    } catch {
      /* noop */
    }
    try {
      this.source?.disconnect();
    } catch {
      /* noop */
    }
    this.stream?.getTracks().forEach((t) => t.stop());
    this.capCtx?.close().catch(() => {});
    this.encoder?.close();
    this.workletNode = undefined;
    this.scriptNode = undefined;
    this.source = undefined;
    this.capCtx = undefined;
    this.stream = undefined;
    this.encoder = undefined;
    this.resampler = null;
    this.transport = undefined;
  }
}

// ── Receiver ─────────────────────────────────────────────────────────────────
/**
 * Plays back one teammate's transmission. Created on `begin`, fed each packet,
 * closed on `end`. Decoded PCM is scheduled on the Web Audio master bus (so the
 * play-test "solo" mute and keep-alive apply) at each packet's timestamp behind
 * a 250 ms jitter buffer — DTX silence gaps reproduce as real silence.
 */
export class TalkReceiver {
  private decoder?: OpusDecoder;
  private ready = false;
  private readonly pending: { bytes: Uint8Array; tsUs: number }[] = [];
  private baseTsUs?: number;
  private startCtxTime?: number;
  private lastEndTime = 0;
  private scheduledAny = false;
  private ended = false;
  private finished = false;
  private stallTimer?: ReturnType<typeof setTimeout>;
  private maxTimer?: ReturnType<typeof setTimeout>;

  constructor(private onDone: () => void) {
    void sfx.playOneShot('talk_begin', 0);
    this.maxTimer = setTimeout(() => this.finish(), RECEIVER_MAX_MS);
    this.resetStall();
    createVoiceDecoder(ENC_RATE).then(
      (dec) => {
        if (this.finished) {
          dec.close();
          return;
        }
        this.decoder = dec;
        this.ready = true;
        for (const p of this.pending) this.decodeAndSchedule(p.bytes, p.tsUs);
        this.pending.length = 0;
        if (this.ended) this.scheduleFinish();
      },
      () => this.finish(), // no codec → just sign off
    );
  }

  /** Feed one packet (bytes + its µs timestamp). */
  push(bytes: Uint8Array, tsUs: number): void {
    if (this.finished) return;
    this.resetStall();
    if (this.ready) this.decodeAndSchedule(bytes, tsUs);
    else this.pending.push({ bytes, tsUs });
  }

  /** Transmission finished — sign off once the buffer has drained. */
  end(): void {
    if (this.finished || this.ended) return;
    this.ended = true;
    if (this.ready) this.scheduleFinish();
  }

  /** Tear down immediately, no sign-off (e.g. replaced by a newer transmission). */
  dispose(): void {
    if (this.finished) return;
    this.finished = true;
    this.clearTimers();
    this.decoder?.close();
    this.decoder = undefined;
  }

  // ── internals ──────────────────────────────────────────────────────────────
  private decodeAndSchedule(bytes: Uint8Array, tsUs: number): void {
    if (this.finished || !this.decoder) return;
    let pcm: Int16Array;
    try {
      pcm = this.decoder.decode(bytes);
    } catch {
      return;
    }
    const frames = pcm.length;
    if (!frames) return;
    const ctx = audioContext();
    let buf: AudioBuffer;
    try {
      buf = ctx.createBuffer(1, frames, ENC_RATE);
    } catch {
      return;
    }
    const ch = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) ch[i] = pcm[i] / 32768;

    if (this.baseTsUs === undefined) {
      this.baseTsUs = tsUs;
      this.startCtxTime = ctx.currentTime + PLAYBACK_BUFFER_MS / 1000;
    }
    let when = this.startCtxTime! + (tsUs - this.baseTsUs) / 1e6;
    if (when < ctx.currentTime) when = ctx.currentTime; // underrun — play asap
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(masterOut());
    src.start(when);
    this.scheduledAny = true;
    this.lastEndTime = Math.max(this.lastEndTime, when + frames / ENC_RATE);
  }

  private scheduleFinish(): void {
    if (this.finished) return;
    if (!this.scheduledAny) {
      this.finish();
      return;
    }
    const ctx = audioContext();
    const delayMs = Math.max(0, (this.lastEndTime - ctx.currentTime) * 1000) + 80;
    if (this.stallTimer) clearTimeout(this.stallTimer);
    this.stallTimer = setTimeout(() => this.finish(), delayMs);
  }

  private resetStall(): void {
    if (this.stallTimer) clearTimeout(this.stallTimer);
    this.stallTimer = setTimeout(() => {
      if (this.ended) this.finish();
      else this.end();
    }, RECEIVER_STALL_MS);
  }

  private finish(): void {
    if (this.finished) return;
    this.finished = true;
    this.clearTimers();
    void sfx.playOneShot('talk_end', 0);
    this.decoder?.close();
    this.decoder = undefined;
    this.onDone();
  }

  private clearTimers(): void {
    if (this.stallTimer) clearTimeout(this.stallTimer);
    if (this.maxTimer) clearTimeout(this.maxTimer);
    this.stallTimer = this.maxTimer = undefined;
  }
}
